import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Bell, BellOff, CheckCheck, Eraser, Trash2 } from "lucide-react";
import { useNotifications } from "@/context/NotificationsContext";
import { useAuth } from "@/context/AuthContext";
import { openTaskWindow } from "@/components/tracker/TaskWindow";
import { NotificationItem } from "@/components/tracker/NotificationItem";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const dayKey = (iso) => { const d = new Date(iso); d.setHours(0, 0, 0, 0); return d.getTime(); };

function groupByDay(rows) {
  const today = dayKey(new Date());
  const buckets = { Today: [], Yesterday: [], Earlier: [] };
  rows.forEach((n) => {
    const k = dayKey(n.created_at);
    buckets[k >= today ? "Today" : k >= today - 86400000 ? "Yesterday" : "Earlier"].push(n);
  });
  return Object.entries(buckets).filter(([, r]) => r.length);
}

function PanelHeader({ unread, tab, setTab, total, onMarkAll }) {
  return (
    <div className="border-b border-white/[0.07] px-4 pb-3 pt-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00FF66]/10 text-[#00FF66] ring-1 ring-[#00FF66]/20"><Bell className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[15px] font-bold leading-tight">Notifications</p>
          <p className="text-[11px] text-white/40" data-testid="notification-subtitle">{unread > 0 ? `${unread} unread` : "You're all caught up"}</p>
        </div>
        {unread > 0 && (
          <button onClick={onMarkAll} className="flex items-center gap-1.5 rounded-full border border-[#00FF66]/30 px-3 py-1.5 text-[11px] font-bold text-[#00FF66] transition-colors duration-150 hover:bg-[#00FF66]/10" data-testid="notification-mark-all-read">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        )}
      </div>
      {total > 0 && (
        <div className="mt-3 flex gap-1 rounded-xl bg-white/[0.04] p-1" data-testid="notification-tabs">
          {[["all", "All", null], ["unread", "Unread", unread]].map(([k, label, n]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-bold transition-colors duration-150 ${tab === k ? "bg-white/10 text-white shadow-sm" : "text-white/45 hover:text-white"}`} data-testid={`notification-tab-${k}`}>
              {label}
              {n > 0 && <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${tab === k ? "bg-[#00FF66] text-black" : "bg-white/10 text-white/50"}`}>{n > 99 ? "99+" : n}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ unreadTab }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center" data-testid="notification-empty">
      <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.04] ring-1 ring-white/10">
        <BellOff className="h-6 w-6 text-white/30" />
        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#00FF66] text-black"><CheckCheck className="h-3 w-3" /></span>
      </span>
      <p className="font-display mt-4 text-sm font-bold">{unreadTab ? "No unread notifications" : "You're all caught up"}</p>
      <p className="mt-1 text-xs text-white/35">{unreadTab ? "Everything here has been read." : "New assignments, replies and reviews will show up here."}</p>
    </div>
  );
}

function PanelFooter({ count, hasRead, onClearRead, onClearAll }) {
  return (
    <div className="flex items-center justify-between border-t border-white/[0.07] bg-white/[0.015] px-4 py-2.5">
      <span className="text-[10px] font-medium text-white/30">{count >= 100 ? "Showing the latest 100" : `${count} notification${count === 1 ? "" : "s"}`}</span>
      <div className="flex items-center gap-1">
        {hasRead && (
          <button onClick={onClearRead} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white/45 transition-colors duration-150 hover:bg-white/5 hover:text-white" data-testid="notification-clear-read" title="Remove notifications you've already read">
            <Eraser className="h-3 w-3" /> Clear read
          </button>
        )}
        <button onClick={onClearAll} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white/45 transition-colors duration-150 hover:bg-[#FF8B94]/10 hover:text-[#FF8B94]" data-testid="notification-clear-all" title="Remove every notification">
          <Trash2 className="h-3 w-3" /> Clear all
        </button>
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user, switchWorkspace } = useAuth();
  const { items, unread, loadItems, markRead, markAll, remove, clearRead, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const hasRead = items.some((n) => n.read);
  const shown = tab === "unread" ? items.filter((n) => !n.read) : items;
  const groups = groupByDay(shown);

  useEffect(() => {
    if (!open) return undefined;
    loadItems();
    const t = setInterval(loadItems, 20000);
    return () => clearInterval(t);
  }, [open, loadItems]);

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) markRead(n.id);
    // A notification from another workspace: switch into it first, then open the link.
    if (n.foreign && n.workspace_id && n.workspace_id !== user?.workspace_id) {
      try { await switchWorkspace(n.workspace_id); } catch { /* fall through to reload */ }
      window.location.assign(n.link || "/app");
      return;
    }
    const taskId = n.link && new URL(n.link, window.location.origin).searchParams.get("task");
    if (taskId) openTaskWindow(taskId);
    else if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative rounded-lg p-2 text-white/60 transition-colors duration-200 hover:bg-white/5 hover:text-white"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF8B94] px-1 text-[9px] font-black text-black"
              data-testid="notification-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-[440px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border-white/10 bg-[#0b0f0d]/95 p-0 text-white shadow-[0_40px_100px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl"
        data-testid="notification-panel"
      >
        <PanelHeader unread={unread} tab={tab} setTab={setTab} total={items.length} onMarkAll={markAll} />
        <div className="max-h-[min(30rem,65vh)] overflow-y-auto px-2 pb-2" data-testid="notification-list">
          {shown.length === 0 ? (
            <EmptyState unreadTab={tab === "unread" && items.length > 0} />
          ) : (
            groups.map(([label, rows]) => (
              <section key={label} data-testid={`notification-group-${label.toLowerCase()}`}>
                <p className="sticky top-0 z-10 -mx-2 bg-[#0b0f0d] px-5 pb-1.5 pt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">{label}</p>
                <ul className="space-y-0.5">
                  <AnimatePresence initial={false}>
                    {rows.map((n, i) => <NotificationItem key={n.id} n={n} index={i} onOpen={openNotification} onDismiss={(x) => remove(x.id, x.read)} />)}
                  </AnimatePresence>
                </ul>
              </section>
            ))
          )}
        </div>
        {items.length > 0 && <PanelFooter count={items.length} hasRead={hasRead} onClearRead={clearRead} onClearAll={clearAll} />}
      </PopoverContent>
    </Popover>
  );
}
